import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateTruckCompanyMutation, useUpdateTruckCompanyMutation } from '../../../../hooks/mutations/useTruckCompanyMutation';
import { TruckCompany } from '../../../../services/apiSLTP/materials/trucksCompanies';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    truckCompany: TruckCompany | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateTruckCompanyModal: React.FC<ModalProps> = ({ truckCompany, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(truckCompany?.designation || '');
    const [email, setEmail] = useState(truckCompany?.email || '');
    const [isActive, setIsActive] = useState(truckCompany?.crudStatus ? truckCompany?.crudStatus === crudStatusEnum.OK : true);
    const [isOwnCompany, setIsOwnCompany] = useState(truckCompany?.isOwnCompany || false);

    const {
        mutate: mutateTruckCompany,
        isSuccess: isSuccessTruckCompany,
        isError: isErrorTruckCompany,
        isLoading: isLoadingTruckCompany,
        error: errorTruckCompany,
        data: dataTruckCompany,
    } = useUpdateTruckCompanyMutation({
        onSuccess: () => refetch(),
        onError: (error) => {
            console.error('Error updating truckCompany:', error.message);
        },
    });

    const {
        mutate: addTruckCompany,
        isSuccess: isSuccessAdded,
        isError: isErrorAdded,
        isLoading: isLoadingAdded,
        error: errorAdded,
        data: dataAdded,
    } = useCreateTruckCompanyMutation({
        onSuccess: () => refetch(),
        onError: (error) => {
            console.error('Error adding truckCompany:', error.message);
        },
    });

    const handleSubmit = () => {
        if (isEditMode) {
            if (!truckCompany) return;
            mutateTruckCompany({
                truckCompanyId: truckCompany._id,
                data: { designation, crudStatus: isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE, email, isOwnCompany },
            });
        } else {
            addTruckCompany({ designation, crudStatus: isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE, email, isOwnCompany });
        }
    };

    return (
        <ModalBody
            title={t('truckCompany')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!designation.length}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={setDesignation} required />
            <ToggleLine checked={isActive} onChange={setIsActive} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput value={email} label={t('email')} onChange={setEmail} />
            <ToggleLine checked={isOwnCompany} onChange={setIsOwnCompany} title={capitalizeFirstLetter(t('isOwnCompany'))} />
        </ModalBody>
    );
};

export default UpdateTruckCompanyModal;
